import { Flex, Text } from '@chakra-ui/react';
import { faEnvelope } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, ButtonText, Modal, useToast } from '@morressier/alembic';
import { IDecisionModalProps } from '../decisionModalHelper';
import { useAuthorCompleteChangeRequest } from 'src/api/commands';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import { MY_SUBMISSION_TABS } from 'src/constants';
import { useQueryClient } from '@tanstack/react-query';

export const AuthorCompleteChangeRequest = ({
  onClose,
  submissionId,
  submissionContextId,
  setActiveTab
}: IDecisionModalProps) => {
  const queryClient = useQueryClient();
  const {
    workflowResponse,
    changeRequestData,
    refetchSubmission,
    organizationId
  } = useSubmissionData();
  const articleWorkflowId = workflowResponse?.articleWorkflow?.id!;

  const toast = useToast();
  const completeChangeRequestMutation = useAuthorCompleteChangeRequest({
    submissionId,
    organizationId,
    submissionContextId,
    subtype: 'form',
    articleWorkflowId,
    invalidateSubmissionQueries() {
      queryClient.invalidateQueries([
        'change-requests',
        { submissionId, roleType: 'author' }
      ]);
      queryClient.invalidateQueries(['requested-change-fields', submissionId]);
    }
  });

  const handleCompleteChangeRequest = async () => {
    const totalRequestedChanges =
      changeRequestData?.changeRequest.fields.length;

    if (totalRequestedChanges !== changeRequestData?.totalAddressedRequests) {
      toast({
        variant: 'error',
        title: (
          <Flex justifyContent="space-between" alignItems="center" gap={2}>
            <Text textStyle="headingItem">Address All Change Requests</Text>
            <ButtonText
              onClick={() => {
                setActiveTab?.(MY_SUBMISSION_TABS.FEEDBACK);
              }}
            >
              View Request
            </ButtonText>
          </Flex>
        ),
        description: 'Some fields weren’t yet addressed.'
      });
      onClose();
      return;
    }

    const res = await completeChangeRequestMutation.mutateAsync({});

    if (!res.isSuccess) {
      toast({
        variant: 'error',
        title: 'Error while submitting changes.',
        description: 'Please try again later.'
      });
      return;
    }

    toast({
      variant: 'success',
      title: 'Changes Submitted',
      description:
        'The Editors have been notified via email to review your submitted changes'
    });
    refetchSubmission();
    onClose();
  };

  return (
    <Modal
      isOpen
      onClose={() => onClose}
      width="720px"
      closeButtonProps={{
        hidden: true
      }}
    >
      <Flex padding={8} gap={6} flexDirection="column">
        <Flex>
          <FontAwesomeIcon fontSize={32} icon={faEnvelope} />
        </Flex>
        <Flex flexDirection="column" gap={2}>
          <Text textStyle="headingGroup">Submit Changes?</Text>
          <Text>
            Proceeding with this decision confirms you have addressed all
            requested changes before resubmitting to the editors.
          </Text>
        </Flex>

        <Flex gap={2} justifyContent="flex-end">
          <Button onClick={onClose} variant="secondary">
            Cancel
          </Button>
          <Button onClick={handleCompleteChangeRequest}>Submit Changes</Button>
        </Flex>
      </Flex>
    </Modal>
  );
};
